import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  PlayCircle,
  CreditCard,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  ArrowRight,
  Camera,
  Gift,
  DoorOpen,
  Clock,
  AlertTriangle,
  Info,
  ChevronRight,
  Zap,
  History,
  LogOut,
  Shield,
  Search
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function TestControlPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Test data state
  const [vrm, setVrm] = useState("TEST001");
  const [siteId, setSiteId] = useState("site_001");
  const [retailerId, setRetailerId] = useState("");
  const [amount, setAmount] = useState(5.00);
  
  // Live data
  const [sessions, setSessions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [exitAuths, setExitAuths] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [retailers, setRetailers] = useState([]);
  
  // Selected session for detail view
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDecision, setSessionDecision] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      fetchAllData();
    } catch (err) {
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = useCallback(async () => {
    try {
      const [sessionsRes, paymentsRes, exitAuthsRes, retailersRes, breachesRes] = await Promise.all([
        axios.get(`${API}/parking/sessions?limit=20`, { withCredentials: true }).catch(() => ({ data: { sessions: [] } })),
        axios.get(`${API}/payment/transactions?limit=20`, { withCredentials: true }).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/exit-auth?limit=20`, { withCredentials: true }).catch(() => ({ data: { authorisations: [] } })),
        axios.get(`${API}/retailers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/designated/breaches?limit=10`, { withCredentials: true }).catch(() => ({ data: { breaches: [] } }))
      ]);
      
      setSessions(sessionsRes.data.sessions || []);
      setPayments(paymentsRes.data.transactions || []);
      setExitAuths(exitAuthsRes.data.authorisations || []);
      setRetailers(Array.isArray(retailersRes.data) ? retailersRes.data : retailersRes.data.retailers || []);
      setBreaches(breachesRes.data.breaches || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  }, []);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (user) {
      const interval = setInterval(fetchAllData, 5000);
      return () => clearInterval(interval);
    }
  }, [user, fetchAllData]);

  // ============== TEST ACTIONS ==============
  
  const createSession = async () => {
    setActionLoading(true);
    try {
      const normalizedVrm = vrm.toUpperCase().replace(/\s/g, '');
      console.log("Creating session with VRM:", normalizedVrm);
      
      // Simulate ANPR entry via webhook - payload format
      const res = await axios.post(`${API}/anpr/webhook/entry`, {
        site_id: siteId,
        lane_id: "lane_entry_1",
        provider: "generic",
        payload: {
          vrm: normalizedVrm,
          confidence: 0.99,
          timestamp: new Date().toISOString()
        }
      }, { withCredentials: true });
      
      console.log("Session created:", res.data);
      toast.success(`Session created for ${vrm}`);
      fetchAllData();
    } catch (err) {
      console.error("Create session error:", err.response?.data);
      const errMsg = err.response?.data?.detail;
      toast.error(typeof errMsg === 'string' ? errMsg : "Failed to create session");
    } finally {
      setActionLoading(false);
    }
  };

  const simulateANPR = async (type = "entry") => {
    setActionLoading(true);
    try {
      const normalizedVrm = vrm.toUpperCase().replace(/\s/g, '');
      console.log(`Simulating ANPR ${type} with VRM:`, normalizedVrm);
      
      const endpoint = type === "entry" ? "webhook/entry" : "webhook/exit";
      const res = await axios.post(`${API}/anpr/${endpoint}`, {
        site_id: siteId,
        lane_id: type === "entry" ? "lane_entry_1" : "lane_exit_1",
        provider: "generic",
        payload: {
          vrm: normalizedVrm,
          confidence: 0.98,
          timestamp: new Date().toISOString()
        }
      }, { withCredentials: true });
      
      console.log("ANPR response:", res.data);
      toast.success(`ANPR ${type} event simulated for ${vrm}`);
      fetchAllData();
    } catch (err) {
      console.error("ANPR error:", err.response?.data);
      const errMsg = err.response?.data?.detail;
      toast.error(typeof errMsg === 'string' ? errMsg : `Failed to simulate ANPR ${type}`);
    } finally {
      setActionLoading(false);
    }
  };

  const applyRetailerBenefit = async () => {
    if (!retailerId) {
      toast.error("Select a retailer first");
      return;
    }
    
    setActionLoading(true);
    try {
      // Find active session for this VRM
      const lookupRes = await axios.get(`${API}/parking/lookup?vrm=${vrm}&site_id=${siteId}`, { withCredentials: true });
      
      if (!lookupRes.data.found) {
        toast.error("No active session found for this vehicle");
        setActionLoading(false);
        return;
      }
      
      const sessionId = lookupRes.data.session_id;
      
      // Apply benefit via settlement engine
      const res = await axios.post(`${API}/parking/benefits`, {
        session_id: sessionId,
        benefit_type: "retailer",
        benefit_value: 2,
        benefit_unit: "hours",
        source: `retailer:${retailerId}`,
        source_reference: `test_qr_${Date.now()}`
      }, { withCredentials: true });
      
      toast.success("Retailer benefit applied!");
      fetchAllData();
    } catch (err) {
      const errMsg = err.response?.data?.detail;
      toast.error(typeof errMsg === 'string' ? errMsg : "Failed to apply benefit");
    } finally {
      setActionLoading(false);
    }
  };

  const triggerTestPayment = async () => {
    setActionLoading(true);
    try {
      // Find active session
      const lookupRes = await axios.get(`${API}/parking/lookup?vrm=${vrm}&site_id=${siteId}`, { withCredentials: true });
      
      if (!lookupRes.data.found) {
        toast.error("No active session found");
        setActionLoading(false);
        return;
      }
      
      // Create test payment
      const res = await axios.post(`${API}/parking/test`, {
        session_id: lookupRes.data.session_id,
        amount: parseFloat(amount),
        test_mode: true
      }, { withCredentials: true });
      
      toast.success(`Test payment of £${amount} processed!`);
      fetchAllData();
    } catch (err) {
      const errMsg = err.response?.data?.detail;
      toast.error(typeof errMsg === 'string' ? errMsg : "Failed to process payment");
    } finally {
      setActionLoading(false);
    }
  };

  const checkExitAuth = async () => {
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/exit-auth/check?vrm=${vrm}&site_id=${siteId}`, { withCredentials: true });
      
      if (res.data.authorised) {
        toast.success(`EXIT AUTHORISED: ${res.data.reason || 'Session settled'}`);
      } else {
        toast.error(`EXIT DENIED: ${res.data.reason || 'Outstanding balance'}`);
      }
      
      fetchAllData();
    } catch (err) {
      const errMsg = err.response?.data?.detail;
      toast.error(typeof errMsg === 'string' ? errMsg : "Failed to check exit auth");
    } finally {
      setActionLoading(false);
    }
  };

  const loadSessionDecision = async (session) => {
    setSelectedSession(session);
    try {
      // Get settlement calculation
      const calcRes = await axios.get(`${API}/parking/calculate/${session.session_id}`, { withCredentials: true });
      
      // Get exit auth status
      const authRes = await axios.get(`${API}/exit-auth/check?vrm=${session.vehicle_registration}&site_id=${session.site_id}`, { withCredentials: true });
      
      setSessionDecision({
        calculation: calcRes.data,
        exitAuth: authRes.data
      });
    } catch (err) {
      console.error("Error loading session decision:", err);
      setSessionDecision(null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/admin/login");
    } catch (err) {
      navigate("/admin/login");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <ParkWiseLogo className="h-8 w-auto" />
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/50">
                  <Zap className="w-3 h-3 mr-1" />
                  Test Mode
                </Badge>
                <span className="text-slate-400">|</span>
                <span className="font-semibold text-lg">Control Panel</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={fetchAllData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Link to="/admin">
                <Button variant="outline" size="sm">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Test Controls Section */}
        <section className="mb-8">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-cyan-400" />
                Test Control Panel
              </CardTitle>
              <CardDescription>
                Simulate the full parking flow without hardware. Data updates live.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <div>
                  <Label className="text-slate-300 font-medium">Vehicle Registration</Label>
                  <Input 
                    value={vrm} 
                    onChange={(e) => setVrm(e.target.value.toUpperCase())}
                    placeholder="AB12 CDE"
                    className="bg-slate-800 border-slate-600 mt-1 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 font-medium">Site ID</Label>
                  <Input 
                    value={siteId} 
                    onChange={(e) => setSiteId(e.target.value)}
                    placeholder="site_001"
                    className="bg-slate-800 border-slate-600 mt-1 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 font-medium">Retailer</Label>
                  <select 
                    value={retailerId}
                    onChange={(e) => setRetailerId(e.target.value)}
                    className="w-full h-10 px-3 mt-1 bg-slate-800 border border-slate-600 rounded-md text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-slate-800 text-slate-400">
                      {retailers.length === 0 ? "No retailers available" : "Select retailer..."}
                    </option>
                    {retailers.map(r => (
                      <option key={r.retailer_id} value={r.retailer_id} className="bg-slate-800 text-white">
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300 font-medium">Payment Amount (£)</Label>
                  <Input 
                    type="number"
                    value={amount} 
                    onChange={(e) => setAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    className="bg-slate-800 border-slate-600 mt-1 text-white placeholder:text-slate-500 focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={createSession}
                  disabled={actionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="create-session-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Car className="w-4 h-4 mr-2" />}
                  1. Create Session (ANPR Entry)
                </Button>
                
                <Button 
                  onClick={() => simulateANPR("entry")}
                  disabled={actionLoading}
                  variant="outline"
                  className="border-cyan-600 text-cyan-400 hover:bg-cyan-600/20"
                  data-testid="simulate-anpr-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Simulate ANPR
                </Button>
                
                <Button 
                  onClick={applyRetailerBenefit}
                  disabled={actionLoading || !retailerId}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="apply-benefit-btn"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  2. Apply Retailer Benefit
                </Button>
                
                <Button 
                  onClick={triggerTestPayment}
                  disabled={actionLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="trigger-payment-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  3. Trigger Payment
                </Button>
                
                <Button 
                  onClick={checkExitAuth}
                  disabled={actionLoading}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="check-exit-btn"
                >
                  <DoorOpen className="w-4 h-4 mr-2" />
                  4. Check Exit Auth
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Data Tabs */}
        <Tabs defaultValue="sessions" className="space-y-4">
          <TabsList className="bg-slate-900/50 border border-slate-800">
            <TabsTrigger value="sessions" className="data-[state=active]:bg-cyan-600">
              <Car className="w-4 h-4 mr-2" />
              Sessions ({sessions.length})
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-cyan-600">
              <CreditCard className="w-4 h-4 mr-2" />
              Payments ({payments.length})
            </TabsTrigger>
            <TabsTrigger value="exitauths" className="data-[state=active]:bg-cyan-600">
              <DoorOpen className="w-4 h-4 mr-2" />
              Exit Auths ({exitAuths.length})
            </TabsTrigger>
            <TabsTrigger value="breaches" className="data-[state=active]:bg-cyan-600">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Breaches ({breaches.length})
            </TabsTrigger>
          </TabsList>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Session List */}
              <div className="lg:col-span-2">
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">Active Sessions</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-800">
                      {sessions.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">
                          No active sessions. Create one using the control panel above.
                        </div>
                      ) : (
                        sessions.map(session => (
                          <div 
                            key={session.session_id}
                            onClick={() => loadSessionDecision(session)}
                            className={`p-4 cursor-pointer hover:bg-slate-800/50 transition-colors ${
                              selectedSession?.session_id === session.session_id ? 'bg-slate-800/70 border-l-4 border-cyan-500' : ''
                            }`}
                            data-testid={`session-row-${session.session_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                                  <Car className="w-5 h-5 text-cyan-400" />
                                </div>
                                <div>
                                  <div className="font-mono font-semibold">{session.vehicle_registration}</div>
                                  <div className="text-sm text-slate-400">
                                    {session.site_id} • {new Date(session.entry_time).toLocaleTimeString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className="font-semibold">
                                    £{(session.amount_due || 0).toFixed(2)}
                                  </div>
                                  <Badge variant={session.status === 'active' ? 'default' : 'secondary'}>
                                    {session.status}
                                  </Badge>
                                </div>
                                <ChevronRight className="w-5 h-5 text-slate-500" />
                              </div>
                            </div>
                            {session.benefits_applied?.length > 0 && (
                              <div className="mt-2 flex gap-2">
                                {session.benefits_applied.map((b, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-purple-500/20 text-purple-400 border-purple-500/50">
                                    <Gift className="w-3 h-3 mr-1" />
                                    {b.type}: {b.value} {b.unit}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Session Decision Panel */}
              <div className="lg:col-span-1">
                <Card className="bg-slate-900/50 border-slate-800 sticky top-24">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Info className="w-5 h-5 text-cyan-400" />
                      Explain Decision
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!selectedSession ? (
                      <div className="text-center text-slate-500 py-8">
                        Click a session to see why payment is required or exit allowed/denied
                      </div>
                    ) : sessionDecision ? (
                      <div className="space-y-4">
                        {/* VRM */}
                        <div className="text-center p-3 bg-slate-800 rounded-lg">
                          <div className="text-2xl font-mono font-bold">{selectedSession.vehicle_registration}</div>
                        </div>

                        {/* Exit Status */}
                        <div className={`p-4 rounded-lg ${sessionDecision.exitAuth?.authorised ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-red-500/20 border border-red-500/50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            {sessionDecision.exitAuth?.authorised ? (
                              <CheckCircle className="w-5 h-5 text-emerald-400" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400" />
                            )}
                            <span className="font-semibold">
                              {sessionDecision.exitAuth?.authorised ? 'EXIT AUTHORISED' : 'EXIT DENIED'}
                            </span>
                          </div>
                          <div className="text-sm text-slate-300">
                            {sessionDecision.exitAuth?.reason || 'No specific reason'}
                          </div>
                        </div>

                        {/* Calculation Breakdown */}
                        <div className="space-y-2">
                          <div className="text-sm font-semibold text-slate-400">Calculation Breakdown</div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-400">Duration:</span>
                              <span>{sessionDecision.calculation?.duration_hours?.toFixed(2) || 0} hrs</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Base Cost:</span>
                              <span>£{sessionDecision.calculation?.base_cost?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Benefits:</span>
                              <span className="text-green-400">-£{sessionDecision.calculation?.total_benefits?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Amount Paid:</span>
                              <span>£{sessionDecision.calculation?.amount_paid?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t border-slate-700 pt-2 mt-2">
                              <span>Outstanding:</span>
                              <span className={sessionDecision.calculation?.outstanding > 0 ? 'text-red-400' : 'text-green-400'}>
                                £{sessionDecision.calculation?.outstanding?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Benefits Applied */}
                        {sessionDecision.calculation?.benefits?.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-semibold text-slate-400">Benefits Applied</div>
                            {sessionDecision.calculation.benefits.map((b, i) => (
                              <div key={i} className="p-2 bg-purple-500/20 rounded border border-purple-500/30 text-sm">
                                <div className="flex justify-between">
                                  <span>{b.type}</span>
                                  <span>{b.value} {b.unit}</span>
                                </div>
                                <div className="text-xs text-slate-400">{b.source}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Why Payment Required */}
                        {sessionDecision.calculation?.outstanding > 0 && (
                          <div className="p-3 bg-amber-500/20 rounded-lg border border-amber-500/30">
                            <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold mb-1">
                              <AlertTriangle className="w-4 h-4" />
                              Why Payment Required
                            </div>
                            <div className="text-sm text-slate-300">
                              {sessionDecision.calculation?.duration_hours > 0 
                                ? `Parked for ${sessionDecision.calculation.duration_hours.toFixed(1)} hours. Standard rate: £2/hr after first free hour.`
                                : 'Session duration requires payment.'
                              }
                              {sessionDecision.calculation?.total_benefits > 0 && (
                                <div className="mt-1 text-green-400">
                                  Benefits saved £{sessionDecision.calculation.total_benefits.toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle>Payment Transactions</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-800">
                  {payments.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No payments yet. Trigger a test payment from the control panel.
                    </div>
                  ) : (
                    payments.map(payment => (
                      <div key={payment.payment_id} className="p-4 hover:bg-slate-800/50" data-testid={`payment-row-${payment.payment_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              payment.status === 'completed' ? 'bg-emerald-500/20' : 
                              payment.status === 'failed' ? 'bg-red-500/20' : 'bg-amber-500/20'
                            }`}>
                              <CreditCard className={`w-5 h-5 ${
                                payment.status === 'completed' ? 'text-emerald-400' : 
                                payment.status === 'failed' ? 'text-red-400' : 'text-amber-400'
                              }`} />
                            </div>
                            <div>
                              <div className="font-mono">{payment.payment_id}</div>
                              <div className="text-sm text-slate-400">
                                {payment.session_id} • {new Date(payment.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="font-semibold text-lg">£{payment.amount?.toFixed(2)}</div>
                              <Badge variant={payment.status === 'completed' ? 'default' : 'secondary'}>
                                {payment.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Exit Auths Tab */}
          <TabsContent value="exitauths">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle>Exit Authorisations</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-800">
                  {exitAuths.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No exit authorisations. Complete a payment to generate one.
                    </div>
                  ) : (
                    exitAuths.map(auth => (
                      <div key={auth.exit_auth_id} className="p-4 hover:bg-slate-800/50" data-testid={`exitauth-row-${auth.exit_auth_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              auth.status === 'valid' ? 'bg-emerald-500/20' : 'bg-slate-700'
                            }`}>
                              <DoorOpen className={`w-5 h-5 ${
                                auth.status === 'valid' ? 'text-emerald-400' : 'text-slate-400'
                              }`} />
                            </div>
                            <div>
                              <div className="font-mono font-semibold">{auth.vehicle_registration}</div>
                              <div className="text-sm text-slate-400">
                                {auth.source} • Expires: {new Date(auth.expires_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <Badge variant={auth.status === 'valid' ? 'default' : 'secondary'} className={
                            auth.status === 'valid' ? 'bg-emerald-500' : ''
                          }>
                            {auth.status}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Breaches Tab */}
          <TabsContent value="breaches">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle>Designated Space Breaches</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-800">
                  {breaches.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No breaches recorded. A vehicle parking in a designated space without entitlement creates a breach.
                    </div>
                  ) : (
                    breaches.map(breach => (
                      <div key={breach.event_id} className="p-4 hover:bg-slate-800/50" data-testid={`breach-row-${breach.event_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                              <AlertTriangle className="w-5 h-5 text-red-400" />
                            </div>
                            <div>
                              <div className="font-mono font-semibold">{breach.vehicle_registration}</div>
                              <div className="text-sm text-slate-400">
                                {breach.zone_type} zone • {new Date(breach.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <Badge variant="destructive">{breach.validation_reason}</Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Stats */}
        <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className="bg-slate-900/50 border-slate-800 cursor-pointer hover:border-cyan-500/50 transition-colors"
            onClick={() => navigate('/admin/freeflow')}
            data-testid="stat-sessions"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{sessions.filter(s => s.status === 'active').length}</div>
                  <div className="text-sm text-slate-400">Active Sessions</div>
                </div>
                <Car className="w-8 h-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-900/50 border-slate-800 cursor-pointer hover:border-emerald-500/50 transition-colors"
            onClick={() => navigate('/admin/billing')}
            data-testid="stat-payments"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{payments.filter(p => p.status === 'completed').length}</div>
                  <div className="text-sm text-slate-400">Payments Today</div>
                </div>
                <CreditCard className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-900/50 border-slate-800 cursor-pointer hover:border-purple-500/50 transition-colors"
            onClick={() => navigate('/admin/exit-auths')}
            data-testid="stat-exitauths"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{exitAuths.filter(a => a.status === 'valid').length}</div>
                  <div className="text-sm text-slate-400">Valid Exit Auths</div>
                </div>
                <DoorOpen className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-slate-900/50 border-slate-800 cursor-pointer hover:border-red-500/50 transition-colors"
            onClick={() => {}}
            data-testid="stat-breaches"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-red-400">{breaches.length}</div>
                  <div className="text-sm text-slate-400">Breaches</div>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}
